import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import noUserImage from "../public/fakeUser/noUser.webp"
import { 
  signIn, 
  signOut, 
  getCurrentUser, 
  fetchAuthSession,
  fetchUserAttributes,
  confirmSignUp,
  resendSignUpCode
} from 'aws-amplify/auth';
import { client } from '../services/client';
import { getS3ObjectUrl } from '../util/s3Utils.js'; 
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils.js';
import { GET_USER_PROFILE, UPDATE_USER } from '../graphql/queries/authQueries.js';
import { deleteUserAccountSecure } from '../graphql/mutations/deleteMutation.js';
import { toApiBirthDate } from '../util/birthDateUtils.js';
import { mapCognitoErrorToGerman } from '../util/authErrorMessages.js';
import { deleteOwnedMedia, uploadOwnedMedia } from '../util/ownedMedia.js';
import { clearAllBookingPaymentStorage } from '../util/stripeBookingPaymentFlow.js';
import { notifyErrorToast, notifySaveError } from '../util/notifyActionError.js';

const DEFAULT_NOTIFICATION_PREFERENCES = {
  accountActivitiesEmail: true,
  accountActivitiesPush: true,
  accountActivitiesSms: false,
  recommendationsEmail: true,
  recommendationsPush: false,
  offersUpdatesEmail: true,
  offersUpdatesPush: false,
};

const getInitialAuthState = () => {
  // Check if user was previously logged in
  const userId = localStorage.getItem('userId');
  
  if (userId) {
    // User was previously logged in, set status to loading so we check their auth
    return {
      isAuthenticated: false, // Will be updated by checkAuthState
      user: {
        _id: "",
        email: "",
        firstName: "",
        lastName: "",
        birthDate: "",
        phoneNumber: "",
        profilePicture: noUserImage,
        notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
      },
      status: "loading", // Start with loading since we need to verify
      profileUpdateStatus: "idle",
      profileUpdateError: null,
      deleteStatus: "idle",
      deleteError: null,
      error: null,
      requiresEmailConfirmation: false,
      emailToConfirm: null
    };
  }
  
  // No previous login, start with idle
  return {
    isAuthenticated: false,
    user: {
      _id: "",
      email: "",
      firstName: "",
      lastName: "",
      birthDate: "",
      phoneNumber: "",
      profilePicture: noUserImage,
      notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
    },
    status: "idle",
    profileUpdateStatus: "idle",
    profileUpdateError: null,
    deleteStatus: "idle",
    deleteError: null,
    error: null,
    requiresEmailConfirmation: false,
    emailToConfirm: null
  };
};

const initialState = getInitialAuthState();

// Helper function to get the correct image URL based on path format
const getProfilePictureUrl = async (profilePicture) => {
  if (!profilePicture) return noUserImage;
  
  // Always use CloudFront for profile pictures to avoid expiration issues
  if (profilePicture && !profilePicture.startsWith('public/')) {
    return getCloudFrontImageUrl(profilePicture);
  }
  
  // For public/ paths, also use CloudFront instead of signed URLs
  return getCloudFrontImageUrl(profilePicture);
};

const fetchAuthenticatedUserState = async () => {
  const currentUser = await getCurrentUser();
  localStorage.setItem('userId', currentUser.userId);

  const { data } = await client.query({
    query: GET_USER_PROFILE,
    variables: { id: currentUser.userId },
    fetchPolicy: 'network-only',
  });

  if (!data?.getUser) {
    throw new Error('No user data returned from GraphQL');
  }

  const profilePictureUrl = await getProfilePictureUrl(data.getUser.profilePicture);
  const notificationPreferences = data.getUser.notificationPreferences || DEFAULT_NOTIFICATION_PREFERENCES;

  return {
    isAuthenticated: true,
    user: {
      ...data.getUser,
      _id: currentUser.userId,
      profilePicture: profilePictureUrl || noUserImage,
      notificationPreferences,
    },
  };
};

const isUserAlreadyAuthenticatedError = (error) => (
  error?.name === 'UserAlreadyAuthenticatedException'
  || error?.code === 'UserAlreadyAuthenticatedException'
);

const hasOwn = (object, property) => Object.prototype.hasOwnProperty.call(object, property);

const mergeProfileUpdatePayload = async ({ currentUser, updatedUser, updateData, userId }) => {
  const submittedFields = {};
  const fieldsToPreserveWhenMissing = [
    'firstName',
    'lastName',
    'phoneNumber',
    'birthDate',
    'gender',
    'notificationPreferences',
  ];

  fieldsToPreserveWhenMissing.forEach((field) => {
    if (hasOwn(updateData, field)) {
      submittedFields[field] = updateData[field];
    }
  });

  const stableUpdatedUser = Object.entries(updatedUser).reduce((result, [field, value]) => {
    const isMissingResponseValue =
      value === undefined ||
      value === null ||
      (typeof value === 'string' && value.trim() === '');

    if (fieldsToPreserveWhenMissing.includes(field) && isMissingResponseValue && !hasOwn(updateData, field)) {
      return result;
    }

    result[field] = value;
    return result;
  }, {});

  const responseHasProfilePicture = hasOwn(updatedUser, 'profilePicture') && updatedUser.profilePicture;
  const profilePictureUrl = responseHasProfilePicture
    ? await getProfilePictureUrl(updatedUser.profilePicture)
    : currentUser.profilePicture;

  return {
    ...currentUser,
    ...stableUpdatedUser,
    ...submittedFields,
    _id: userId,
    profilePicture: profilePictureUrl || noUserImage,
    notificationPreferences:
      stableUpdatedUser.notificationPreferences ??
      submittedFields.notificationPreferences ??
      currentUser.notificationPreferences ??
      DEFAULT_NOTIFICATION_PREFERENCES,
  };
};

export const checkAuthState = createAsyncThunk(
  "auth/checkAuthState",
  async () => {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens?.accessToken) {
        return null;
      }

      return await fetchAuthenticatedUserState();
    } catch (error) {
      return null;
    }
  }
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (values, { rejectWithValue }) => {
    try {
      const attributes = values?.attributes || {};
      const requiredText = (value) => (typeof value === 'string' ? value.trim() : '');
      const givenName = requiredText(attributes.given_name);
      const familyName = requiredText(attributes.family_name);
      const birthDate = requiredText(attributes.birthDate);

      if (!givenName || !familyName || !birthDate) {
        throw new Error('Profilangaben fehlen. Bitte fülle Vorname, Nachname und Geburtsdatum aus.');
      }

      // Get the current user ID after they've already signed in
      const currentUser = await getCurrentUser();
      const userId = currentUser.userId;
      
      // Prepare update data for the freshly confirmed profile
      const updateData = {
        id: userId,
        firstName: givenName,
        lastName: familyName,
        phoneNumber: attributes.phone_number,
        birthDate: toApiBirthDate(birthDate)
      };
      
      // Use Apollo client to update the user with the mutation
      const { data } = await client.mutate({
        mutation: UPDATE_USER,
        variables: {
          input: updateData
        }
      });
      
      if (!data?.updateUser) {
        throw new Error('Profil konnte nicht aktualisiert werden.');
      }

      localStorage.setItem('userId', userId);

      // Re-fetch full profile — mutation response can omit fields until persisted
      const { data: profileData } = await client.query({
        query: GET_USER_PROFILE,
        variables: { id: userId },
        fetchPolicy: 'network-only',
      });

      const profile = profileData?.getUser;
      if (!profile) {
        throw new Error('No user data returned after registration');
      }

      const profilePictureUrl = await getProfilePictureUrl(profile.profilePicture);

      return {
        isAuthenticated: true,
        user: {
          ...profile,
          _id: userId,
          profilePicture: profilePictureUrl || noUserImage,
          notificationPreferences:
            profile.notificationPreferences || DEFAULT_NOTIFICATION_PREFERENCES,
        },
      };
    } catch (error) {
      notifyErrorToast(
        mapCognitoErrorToGerman(error, 'Fehler beim Registrieren/Aktualisieren des Profils.'),
        { title: 'Profil konnte nicht gespeichert werden' }
      );
      return rejectWithValue(error.message);
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      let isSignedIn = false;
      let nextStep;

      try {
        const signInResult = await signIn({ username: email, password });
        isSignedIn = signInResult.isSignedIn;
        nextStep = signInResult.nextStep;
      } catch (signInError) {
        if (isUserAlreadyAuthenticatedError(signInError)) {
          return await fetchAuthenticatedUserState();
        }
        throw signInError;
      }

      if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return {
          requiresEmailConfirmation: true,
          email,
          isAuthenticated: false,
        };
      }

      if (isSignedIn) {
        return await fetchAuthenticatedUserState();
      }

      throw new Error('Unexpected sign-in state');
    } catch (error) {
      return rejectWithValue(mapCognitoErrorToGerman(error, 'Anmeldung fehlgeschlagen.'));
    }
  }
);

export const confirmEmailSignUp = createAsyncThunk(
  "auth/confirmEmailSignUp",
  async ({ email, confirmationCode }, { rejectWithValue }) => {
    try {
      // 1. Confirm sign up with the verification code
      await confirmSignUp({
        username: email,
        confirmationCode
      });

      // 2. Return success - user will need to sign in with password after confirmation
      return {
        emailConfirmed: true,
        isAuthenticated: false
      };
    } catch (error) {
      return rejectWithValue(mapCognitoErrorToGerman(error, 'E-Mail konnte nicht bestätigt werden.'));
    }
  }
);

export const resendEmailConfirmation = createAsyncThunk(
  "auth/resendEmailConfirmation",
  async ({ email }, { rejectWithValue }) => {
    try {
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (error) {
      return rejectWithValue(mapCognitoErrorToGerman(error, 'Bestätigungscode konnte nicht erneut gesendet werden.'));
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await signOut();
      localStorage.removeItem('userId');
      clearAllBookingPaymentStorage();
      
      const baseUrl = process.env.PUBLIC_URL || '/';
      window.location.href = baseUrl;
      
      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async ({ userId, reason, feedback }, { rejectWithValue }) => {
    try {
      const variables = { input: { userId, reason, feedback } };

      const { data } = await client.mutate({
        mutation: deleteUserAccountSecure,
        variables,
      });

      const payload = data?.deleteUserAccountSecure;
      if (!payload || !payload.success) {
        notifyErrorToast('Account konnte nicht gelöscht werden. Bitte versuche es erneut.', {
          title: 'Löschen fehlgeschlagen',
        });
        return rejectWithValue({
          code: payload?.code,
          message: 'Account konnte nicht gelöscht werden. Bitte versuche es erneut.'
        });
      }

      // 5) Make sure the authentication state is fully cleared on the client.
      //    This mirrors the behaviour of logoutUser (signOut + localStorage cleanup)
      try {
        await signOut();
      } catch (signOutError) {
        console.warn('Sign-out nach Account-Löschung fehlgeschlagen:', signOutError);
      }

      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('userId');
          clearAllBookingPaymentStorage();
          const baseUrl = process.env.PUBLIC_URL || '/';
          // Hard redirect to ensure we leave any /profile route and clear in‑memory state
          window.location.href = baseUrl;
        } catch (storageError) {
          console.warn('Lokaler Cleanup nach Account-Löschung fehlgeschlagen:', storageError);
        }
      }

      return true;
    } catch (err) {
      // Check if this is a GraphQL error with response data
      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        // Try to extract error information from GraphQL errors
        const graphQLError = err.graphQLErrors[0];
        return rejectWithValue({
          code: graphQLError?.extensions?.code,
          message: 'Account konnte nicht gelöscht werden. Bitte versuche es erneut.'
        });
      }
      
      // For network errors or other errors, return as object for consistency
      return rejectWithValue({
        code: undefined,
        message: 'Account konnte nicht gelöscht werden. Bitte versuche es erneut.'
      });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (formData, { getState, rejectWithValue }) => {
    let uploadedProfilePictureKey = null;
    try {
      const { auth } = getState();
      const userId = auth.user._id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      const updateData = {
        id: userId,
      };

      const updatableFields = [
        'firstName',
        'lastName',
        'phoneNumber',
        'birthDate',
        'gender',
        'notificationPreferences',
      ];

      updatableFields.forEach((field) => {
        if (
          Object.prototype.hasOwnProperty.call(formData, field) &&
          formData[field] !== undefined &&
          formData[field] !== null
        ) {
          updateData[field] =
            field === 'birthDate' ? toApiBirthDate(formData[field]) : formData[field];
        }
      });

      // Handle profile picture upload if provided
      if (formData.profilePicture instanceof File) {
        const uploadResult = await uploadOwnedMedia({
          file: formData.profilePicture,
          entityType: 'USER_PROFILE',
          mediaKind: 'IMAGE',
          slot: 'profile',
          fileName: formData.profilePicture.name || 'profile.jpg',
        });

        uploadedProfilePictureKey = uploadResult.key;
        updateData.profilePicture = uploadedProfilePictureKey;
      }
      
      // Execute the update mutation

      let data;
      try {
        const result = await client.mutate({
          mutation: UPDATE_USER,
          variables: {
            input: updateData
          }
        });
        data = result.data;
      } catch (mutationError) {
        throw mutationError;
      }

      if (!data?.updateUser) {
        throw new Error('Profil konnte nicht aktualisiert werden.');
      }

      return mergeProfileUpdatePayload({
        currentUser: auth.user,
        updatedUser: data.updateUser,
        updateData,
        userId,
      });
    } catch (error) {
      if (uploadedProfilePictureKey) {
        try {
          await deleteOwnedMedia({
            key: uploadedProfilePictureKey,
            entityType: 'USER_PROFILE',
            mediaKind: 'IMAGE',
          });
        } catch (cleanupError) {
          console.warn('Profilbild-Cleanup nach fehlgeschlagenem Profil-Update ist fehlgeschlagen:', cleanupError);
        }
      }
      notifySaveError(error, { title: 'Profil konnte nicht aktualisiert werden' });
      return rejectWithValue(error.message);
    }
  }
);    

export const getUser = createAsyncThunk(
  "auth/getUser",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      
      // Convert profile picture path to signed URL if it exists
      let profilePictureUrl = '';
      if (userAttributes.picture) {
        profilePictureUrl = await getS3ObjectUrl(userAttributes.picture);
      }
      
      return {
        token,
        user: {
          email: userAttributes.email,
          _id: currentUser.userId,
          isAdmin: userAttributes['custom:isAdmin'] === 'true',
          profileImage: profilePictureUrl || userAttributes.picture || '',
          firstName: userAttributes.given_name || '',
          lastName: userAttributes.family_name || '',
          phone: userAttributes.phone_number || '',
          ...userAttributes
        }
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const refreshUserProfile = createAsyncThunk(
  "auth/refreshUserProfile",
  async (_, { getState, dispatch }) => {
    try {
      const { auth } = getState();
      if (!auth.isAuthenticated) return null;
      
      const userId = auth.user._id;
      
      const { data } = await client.query({
        query: GET_USER_PROFILE,
        variables: { id: userId },
        fetchPolicy: 'network-only'
      });
      
      if (!data?.getUser) {
        throw new Error('No user data returned');
      }
      
      // Get profile picture URL using the helper function
      let profilePictureUrl = await getProfilePictureUrl(data.getUser.profilePicture);
      
      return {
        ...data.getUser,
        _id: userId,
        profilePicture: profilePictureUrl || noUserImage,
        notificationPreferences: data.getUser.notificationPreferences || DEFAULT_NOTIFICATION_PREFERENCES,
      };
    } catch (error) {
      notifyErrorToast(error?.message || 'Profil konnte nicht aktualisiert werden.', {
        title: 'Profil konnte nicht geladen werden',
      });
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuth: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        if (action.payload) {
          state.status = "succeeded";
          state.isAuthenticated = action.payload.isAuthenticated;
          state.user = action.payload.user;
          state.error = null;
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isAuthenticated = action.payload.isAuthenticated;
        state.error = null;
        
        // Handle email confirmation requirement
        if (action.payload.requiresEmailConfirmation) {
          state.requiresEmailConfirmation = true;
          state.emailToConfirm = action.payload.email;
        } else {
          state.user = action.payload.user;
          state.requiresEmailConfirmation = false;
          state.emailToConfirm = null;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // Email confirmation cases
      .addCase(confirmEmailSignUp.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(confirmEmailSignUp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.requiresEmailConfirmation = false;
        state.emailToConfirm = null;
        state.error = null;
        // User needs to sign in again with their password after email confirmation
      })
      .addCase(confirmEmailSignUp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Resend email confirmation cases
      .addCase(resendEmailConfirmation.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(resendEmailConfirmation.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        return initialState;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Check Auth cases
      .addCase(checkAuthState.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(checkAuthState.fulfilled, (state, action) => {
        if (action.payload) {
          state.isAuthenticated = action.payload.isAuthenticated;
          state.user = action.payload.user;
          state.status = "succeeded";
        } else {
          state.status = "succeeded";
          state.isAuthenticated = false;
          state.error = null;
          state.user = initialState.user;
        }
      })
      .addCase(checkAuthState.rejected, (state, action) => {
        return initialState;
      })
      .addCase(refreshUserProfile.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
        }
      })
      // Update user profile cases
      .addCase(updateUserProfile.pending, (state) => {
        state.profileUpdateStatus = "loading";
        state.profileUpdateError = null;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileUpdateStatus = "succeeded";
        // Merge updated fields with existing user data to prevent white flash
        state.user = {
          ...state.user,
          ...action.payload
        };
        state.profileUpdateError = null;
        state.error = null;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileUpdateStatus = "failed";
        state.profileUpdateError = action.payload;
        state.error = action.payload;
      })
      // Delete account cases
      .addCase(deleteAccount.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.deleteStatus = "succeeded";
        state.isAuthenticated = false;
        state.user = {
          ...initialState.user,
        };
        state.status = "idle";
        state.error = null;
        if (typeof window !== 'undefined') {
          localStorage.removeItem('userId');
          clearAllBookingPaymentStorage();
        }
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload;
      });
  },
});

export const { clearAuth } = authSlice.actions;
export default authSlice.reducer;
